import React from "react"
import { Field as FieldPrimitive } from "@base-ui/react/field"
import { Icon } from "../icon"
import { cn } from "../utils"
import { useFieldContext } from "./field-context"

// =============================================================================
// Styles
// =============================================================================
const errorStyles = [
  "flex items-start gap-2", // box
  "font-sans text-xs leading-5 font-normal text-gray-900" // typography
]

// =============================================================================
// Props
// =============================================================================
export type FieldErrorProps = {
  /** What is wrong with the value and, ideally, how to fix it */
  children?: React.ReactNode
  /** Additional CSS classes for layout */
  className?: string
}

// =============================================================================
// Component
// =============================================================================
/**
 * The reason a `Field`'s value was rejected, shown under the control while the
 * field is `invalid`.
 *
 * It renders nothing while the field is valid, so it can stay in the tree.
 * A screen reader announces the message as it appears. There is no
 * `aria-describedby` to pass: the message registers its id with the
 * surrounding field while it shows, so it is also read after the control's
 * name.
 *
 * @example
 * ```tsx
 * <Field invalid={Boolean(error)}>
 *   <Field.Label>Reply-to address</Field.Label>
 *   <Input value={value} onValueChange={setValue} />
 *   <Field.Error>{error}</Field.Error>
 * </Field>
 * ```
 */
export const FieldError = React.forwardRef<HTMLDivElement, FieldErrorProps>(
  function FieldError({ children, className }, ref) {
    const { invalid } = useFieldContext()

    // Base UI would otherwise show the error on native validity, and a `false`
    // match counts as unset
    if (!invalid) return null

    return (
      <FieldPrimitive.Error
        ref={ref}
        match
        role="alert"
        data-slot="field-error"
        className={cn(errorStyles, className)}
      >
        <Icon name="alert-triangle" className="shrink-0 text-red-600" />
        {/* Centres the first 20px line on the 24px icon */}
        <span className="min-w-0 flex-1 py-[0.125rem]">{children}</span>
      </FieldPrimitive.Error>
    )
  }
)

FieldError.displayName = "Field.Error"
